/**
 * Billing Settings Page
 *
 * Owner-only page showing:
 * - Current plan, usage bar, season pass status, dunning warnings
 * - Tier comparison cards (incl. the one-time Season Pass)
 * - Real Stripe flows: hosted Checkout for purchases, in-place plan switches,
 *   and the Billing Portal for cancel / payment-method updates
 * - Texting: monthly included texts, text-pack balance and purchase (2026-10-20)
 * - Complimentary orgs (billingExempt, e.g. RMU): no plans to buy, unlimited texts
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useOrganization } from '../../../context/OrganizationContext';
import {
  getSubscription,
  getTiers,
  startCheckout,
  openBillingPortal,
  getSmsUsage,
  smsTextsRemaining,
  SubscriptionDetails,
  TierInfo,
  PlanId,
  SmsUsage,
  SmsPackId,
} from '../../../api/subscription';
import { getErrorMessage } from '../../../api/client';

export default function BillingSettings() {
  const { currentOrg, isOrgOwner } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [smsUsage, setSmsUsage] = useState<SmsUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch subscription and tiers
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subResponse, tiersResponse] = await Promise.all([
        getSubscription(),
        getTiers(),
      ]);
      setSubscription(subResponse.subscription);
      setTiers(tiersResponse.tiers);
      // Texting usage is a separate, best-effort read: a failure here must
      // not blank the rest of the billing page.
      getSmsUsage()
        .then(setSmsUsage)
        .catch(() => setSmsUsage(null));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Returning from Stripe Checkout (?checkout=success|canceled)
  useEffect(() => {
    const outcome = searchParams.get('checkout');
    if (!outcome) return;
    if (outcome === 'success') {
      // The webhook applies the tier change; it usually lands before the
      // redirect completes, but the refetch below covers the race.
      toast.success('Payment complete! Your account has been updated.');
    } else if (outcome === 'canceled') {
      toast('Checkout canceled. No charge was made.');
    }
    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectPlan = async (plan: PlanId) => {
    if (plan === 'FREE') {
      // Downgrading = cancelling the paid subscription, done in the portal so
      // Stripe handles period-end semantics and confirmation.
      return handleOpenPortal();
    }
    setBusyPlan(plan);
    try {
      const result = await startCheckout(plan);
      if (result.url) {
        window.location.assign(result.url);
        return; // navigating away
      }
      if (result.updated) {
        toast.success('Plan updated! The change takes effect immediately.');
        await fetchData();
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyPlan(null);
    }
  };

  const handleBuyPack = async (pack: SmsPackId) => {
    setBusyPlan(pack);
    try {
      const result = await startCheckout(pack);
      if (result.url) {
        window.location.assign(result.url);
        return; // navigating away
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyPlan(null);
    }
  };

  const handleOpenPortal = async () => {
    setBusyPlan('PORTAL');
    // Open the tab synchronously in the click gesture, THEN point it at the
    // portal once the session URL arrives — window.open after an await is
    // what popup blockers eat. The portal opens in its own tab so the app
    // (and its state) stays put; busy state clears in finally so the button
    // can never wedge on "Opening…".
    const portalTab = window.open('', '_blank');
    try {
      const { url } = await openBillingPortal();
      if (portalTab) {
        portalTab.location.href = url;
      } else {
        // Popup blocked — fall back to navigating this tab.
        window.location.assign(url);
      }
    } catch (err) {
      portalTab?.close();
      toast.error(getErrorMessage(err));
    } finally {
      setBusyPlan(null);
    }
  };

  // If the browser restores this page from the back/forward cache (e.g. the
  // user went to Stripe Checkout and came back), React state comes back with
  // it — including a stale busyPlan. Clear it on every pageshow restore.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setBusyPlan(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
        <p className="mt-2 text-gray-600">Loading billing information...</p>
      </div>
    );
  }

  const paymentsEnabled = subscription?.paymentsEnabled ?? false;
  const complimentary = smsUsage?.complimentary ?? false;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/admin/settings" className="hover:text-gray-700">
              Settings
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="w-4 h-4" />
          </li>
          <li className="text-gray-900 font-medium">Billing</li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="text-gray-600 mt-1">
          Manage your subscription plan for {currentOrg?.orgName || 'your organization'}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Owner-only notice */}
      {!isOrgOwner && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium">View Only</p>
              <p className="mt-1">
                Only organization owners can manage billing and subscription settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment-failed warning */}
      {subscription?.subscriptionStatus === 'past_due' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Payment failed</p>
              <p className="mt-1">
                Your last payment didn't go through. We'll retry automatically. Please update
                your payment method to avoid losing paid features.
              </p>
              {isOrgOwner && (
                <button
                  onClick={handleOpenPortal}
                  disabled={busyPlan !== null}
                  className="mt-2 text-sm font-medium text-red-800 underline"
                >
                  Update payment method
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      {subscription && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
              <p className="text-sm text-gray-500">
                Usage month: {formatDate(subscription.billingPeriod.start)} - {formatDate(subscription.billingPeriod.end)}
              </p>
              {subscription.currentPeriodEnd && subscription.subscriptionStatus === 'active' && (
                subscription.cancelAtPeriodEnd ? (
                  <p className="text-sm font-medium text-amber-700">
                    Cancelled — paid access ends {formatDate(subscription.currentPeriodEnd)}, then
                    this organization returns to the Free plan. You will not be charged again.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">
                    Renews {formatDate(subscription.currentPeriodEnd)}
                  </p>
                )
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium ${getTierBadgeClass(subscription.effectiveTier)}`}>
                {subscription.tierName}
              </span>
              {isOrgOwner && paymentsEnabled && subscription.hasStripeCustomer && !complimentary && (
                <button
                  onClick={handleOpenPortal}
                  disabled={busyPlan !== null}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {busyPlan === 'PORTAL' ? 'Opening…' : 'Manage billing'}
                </button>
              )}
            </div>
          </div>

          {complimentary && (
            <div className="mb-4 p-4 bg-white border border-hairline border-l-[3px] border-l-preview text-sm text-ink-body">
              <span className="font-medium text-ink">Complimentary.</span> ProgramRow is provided to{' '}
              {currentOrg?.orgName || 'this organization'} at no charge, including unlimited texts.
              You will never be charged.
            </div>
          )}

          {/* Season pass banner */}
          {subscription.seasonPass && (
            <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
              <span className="font-medium">Season Pass active</span> — Enterprise-level access
              until {formatDate(subscription.seasonPass.expiresAt)}. It will not renew or charge
              you again.
            </div>
          )}

          {/* Usage Section */}
          {!subscription.usage.isUnlimited && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Events this month</span>
                <span className="font-medium text-gray-900">
                  {subscription.usage.used} of {subscription.usage.limit}
                </span>
              </div>
              <div className="w-full bg-gray-200 h-3">
                <div
                  className={`h-3 transition-all ${getUsageBarClass(subscription.usage.used, subscription.usage.limit!)}`}
                  style={{
                    width: `${Math.min(100, (subscription.usage.used / subscription.usage.limit!) * 100)}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {subscription.usage.remaining} events remaining this month
              </p>
            </div>
          )}

          {subscription.usage.isUnlimited && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-green-700 font-medium">
                  Unlimited events
                  {subscription.usage.used > 0 && (
                    <span className="font-normal text-green-600"> — {subscription.usage.used} scheduled this month</span>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Texting */}
      {smsUsage && subscription && (
        <TextingCard
          usage={smsUsage}
          canText={subscription.effectiveTier === 'ENTERPRISE'}
          isOwner={isOrgOwner}
          busyPlan={busyPlan}
          onBuy={handleBuyPack}
        />
      )}

      {/* Tier Comparison (complimentary orgs have nothing to buy) */}
      {!complimentary && (
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              isCurrentTier={
                tier.id === 'SEASON_PASS'
                  ? subscription?.seasonPass != null
                  : // While a season pass is active it IS the org's plan — the
                    // pass card alone carries the badge. (Otherwise the
                    // subscription tier badges; with an active pass, tagging
                    // FREE "Current Plan" reads as a contradiction.)
                    subscription?.seasonPass == null && subscription?.tier === tier.id
              }
              isOwner={isOrgOwner}
              paymentsEnabled={paymentsEnabled}
              busy={busyPlan === tier.id}
              anyBusy={busyPlan !== null}
              onSelect={() => handleSelectPlan(tier.id)}
            />
          ))}
        </div>
      </div>
      )}

      {/* Payments-unconfigured notice (dev instances, or prod pre-keys) */}
      {!paymentsEnabled && !complimentary && (
        <div className="bg-surface-panel border border-hairline border-l-[3px] border-l-rack p-4">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Online payment is not available yet</p>
              <p className="mt-1">
                Email{' '}
                <a href="mailto:billing@programrow.com" className="underline font-medium">
                  billing@programrow.com
                </a>{' '}
                to change your plan.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface TextingCardProps {
  usage: SmsUsage;
  /** Texting is included on Enterprise and the Season Pass. */
  canText: boolean;
  isOwner: boolean;
  busyPlan: string | null;
  onBuy: (pack: SmsPackId) => void;
}

/** Texting allowance: included texts this month, pack balance, buy packs (Terms §5.9). */
export function TextingCard({ usage, canText, isOwner, busyPlan, onBuy }: TextingCardProps) {
  const remaining = smsTextsRemaining(usage);
  const pct = usage.includedAllowance > 0 ? Math.min(100, (usage.includedUsed / usage.includedAllowance) * 100) : 100;
  const paused = remaining === 0;
  const sentThisMonth = Object.values(usage.byKind).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white border border-hairline p-6 mb-6" data-testid="texting-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Texting</h2>
          <p className="text-sm text-gray-500">
            {usage.complimentary
              ? 'Unlimited texts, at no charge.'
              : `${usage.includedAllowance} texts included each month. Resets ${formatDate(usage.resetsAt)}.`}
          </p>
        </div>
      </div>

      {!canText && !usage.complimentary ? (
        <p className="text-sm text-gray-600">
          Text alerts to crew are included on Enterprise and the Season Pass.
        </p>
      ) : usage.complimentary ? (
        <p className="text-sm text-gray-600">
          {sentThisMonth} text{sentThisMonth === 1 ? '' : 's'} sent this month.
        </p>
      ) : (
        <>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Included texts this month</span>
            <span className="font-medium text-gray-900">
              {usage.includedUsed} of {usage.includedAllowance}
            </span>
          </div>
          <div className="w-full bg-gray-200 h-3">
            <div
              className={`h-3 transition-all ${getUsageBarClass(usage.includedUsed, usage.includedAllowance)}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
            <p>
              Text pack balance: <span className="font-medium text-gray-900">{usage.packBalance.toLocaleString('en-US')}</span>
            </p>
            <p>
              Texts left this month: <span className="font-medium text-gray-900">{remaining?.toLocaleString('en-US')}</span>
            </p>
          </div>

          {paused && (
            <div className="mt-4 p-3 bg-white border border-hairline border-l-[3px] border-l-program text-sm text-ink-body">
              <span className="font-medium text-ink">Texting is paused until {formatDate(usage.resetsAt)}.</span>{' '}
              Email and in-app notifications are still going out.
              {usage.blocked > 0 && ` ${usage.blocked} text${usage.blocked === 1 ? ' was' : 's were'} not sent this month.`}
            </div>
          )}

          <div className="mt-5">
            <p className="text-sm font-medium text-gray-900 mb-2">Need more texts?</p>
            <p className="text-sm text-gray-600 mb-3">
              Text packs are one-time payments, used after your included texts, and never expire.
            </p>
            {isOwner && usage.purchasable ? (
              <div className="flex flex-wrap gap-3">
                {usage.packs.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onBuy(p.id)}
                    disabled={busyPlan !== null}
                    className="px-4 py-2 text-sm font-medium border border-rack text-rack hover:bg-rack hover:text-white disabled:opacity-50"
                  >
                    {busyPlan === p.id
                      ? 'Opening checkout…'
                      : `Buy ${p.texts.toLocaleString('en-US')} texts ($${(p.priceCents / 100).toFixed(0)})`}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {isOwner
                  ? 'To buy a text pack, email billing@programrow.com.'
                  : 'Ask an organization owner to add a text pack.'}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface TierCardProps {
  tier: TierInfo;
  isCurrentTier: boolean;
  isOwner: boolean;
  paymentsEnabled: boolean;
  busy: boolean;
  anyBusy: boolean;
  onSelect: () => void;
}

function TierCard({ tier, isCurrentTier, isOwner, paymentsEnabled, busy, anyBusy, onSelect }: TierCardProps) {
  const isPro = tier.id === 'PRO';

  return (
    <div
      className={`relative bg-white rounded-lg shadow-sm border-2 p-6 ${
        isCurrentTier
          ? 'border-primary-500 ring-2 ring-primary-100'
          : isPro
          ? 'border-primary-200'
          : 'border-gray-200'
      }`}
    >
      {/* Popular badge for Pro */}
      {isPro && !isCurrentTier && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-program text-white text-xs font-medium px-3 py-1">
            Most Popular
          </span>
        </div>
      )}

      {/* Current plan badge */}
      {isCurrentTier && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-preview text-white text-xs font-medium px-3 py-1">
            {tier.id === 'SEASON_PASS' ? 'Active' : 'Current Plan'}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6 mt-2">
        <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-gray-900">
            {tier.oneTime ? `$${tier.priceMonthly / 100}` : tier.priceDisplay}
          </span>
          {tier.priceMonthly > 0 && (
            <span className="text-gray-500">{tier.oneTime ? ' one-time' : '/month'}</span>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-500">
          {tier.oneTime
            ? 'Unlimited events for 10 months'
            : tier.monthlyEventLimit
            ? `Up to ${tier.monthlyEventLimit} events/month`
            : 'Unlimited events'}
        </p>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-6">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Action Button */}
      <div>
        {isCurrentTier ? (
          <button
            disabled
            className="w-full py-2 px-4 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed"
          >
            {tier.id === 'SEASON_PASS' ? 'Season Pass Active' : 'Current Plan'}
          </button>
        ) : isOwner && paymentsEnabled ? (
          <button
            disabled={anyBusy}
            className="w-full py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            onClick={onSelect}
          >
            {busy
              ? 'Redirecting…'
              : tier.priceMonthly === 0
              ? 'Downgrade'
              : tier.oneTime
              ? 'Buy Season Pass'
              : 'Upgrade'}
          </button>
        ) : isOwner ? (
          <a
            href="mailto:billing@programrow.com"
            className="block text-center w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Email billing@programrow.com
          </a>
        ) : (
          <button
            disabled
            className="w-full py-2 px-4 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed"
          >
            Contact Owner
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getTierBadgeClass(tier: string): string {
  switch (tier) {
    case 'FREE':
      return 'bg-gray-100 text-gray-700';
    case 'PRO':
      return 'bg-primary-100 text-primary-700';
    case 'ENTERPRISE':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function getUsageBarClass(used: number, limit: number): string {
  const percentage = (used / limit) * 100;
  if (percentage >= 100) return 'bg-red-500';
  if (percentage >= 80) return 'bg-yellow-500';
  return 'bg-rack';
}

// =============================================================================
// Icons
// =============================================================================

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
